// ── sessions-collection: useCollection server adapter for /sessions ─────────

import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  StatusDot,
  useCollection,
  type CollectionColumnDef,
  type CollectionFetcherInput,
  type CollectionFetcherResult,
  type CollectionFilterDef,
  type UseCollectionResult
} from "@vjal/ui";
import type { CoachingSessionDto } from "../../services/coaching-dtos";
import { readDirectoryInitialState } from "../directory-collection-state";
import {
  inviteStatusLabel,
  inviteStatusTone,
  orDash,
  sessionDisplayTitle,
  sessionStatusLabel,
  sessionStatusTone
} from "./sessions-helpers";

export const SESSIONS_PAGE_SIZE = 25;
const DEFAULT_SESSION_STATUSES = ["scheduled", "rescheduled", "completed", "cancelled", "archived", "cleared"];

export type SessionRow = CoachingSessionDto & Record<string, unknown>;

export interface CohortFilterOption {
  id: string;
  label: string;
}

export function cohortOptionsFromSessions(rows: CoachingSessionDto[]): CohortFilterOption[] {
  const seen = new Map<string, string>();
  for (const row of rows) {
    const id = (row.cohort_id ?? "").trim();
    if (!id) continue;
    if (!seen.has(id)) seen.set(id, (row.cohort_name ?? "").trim() || id);
  }
  return Array.from(seen, ([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
}

export function statusOptionsFromSessions(rows: CoachingSessionDto[]): string[] {
  const seen = new Set<string>();
  for (const row of rows) {
    const status = (row.status ?? "").trim();
    if (status) seen.add(status);
  }
  return Array.from(seen).sort();
}

export function buildSessionsFilterDefs(
  rows: CoachingSessionDto[],
  cohortOptionsOverride?: CohortFilterOption[],
  statusOptionsOverride?: string[]
): CollectionFilterDef<SessionRow>[] {
  const cohortOptions = cohortOptionsOverride ?? cohortOptionsFromSessions(rows);
  const statusOptions = statusOptionsOverride ?? (rows.length > 0 ? statusOptionsFromSessions(rows) : DEFAULT_SESSION_STATUSES);
  return [
    {
      id: "status",
      label: "Status",
      type: "select",
      placeholder: "All statuses",
      getValue: (row) => row.status ?? "scheduled",
      options: statusOptions.map((status) => ({ value: status, label: sessionStatusLabel(status) }))
    },
    {
      id: "cohort_id",
      label: "Cohort",
      type: "select",
      placeholder: "All cohorts",
      getValue: (row) => row.cohort_id ?? "",
      options: cohortOptions.map((option) => ({ value: option.id, label: option.label }))
    }
  ];
}

export function buildSessionsCollectionColumns(): CollectionColumnDef<SessionRow>[] {
  return [
    {
      id: "title",
      header: "Session",
      searchValue: (row) => `${row.title ?? ""} ${row.cohort_name ?? ""}`,
      priority: 1,
      cell: (row) => (
        <Link
          to={`/sessions/${encodeURIComponent(row.id)}`}
          className="font-medium text-link no-underline hover:underline"
          data-exact-set-name="Open session">
          {sessionDisplayTitle(row.title, row.id)}
        </Link>
      )
    },
    {
      id: "cohort_name",
      header: "Cohort",
      priority: 2,
      cell: (row) => orDash(row.cohort_name)
    },
    {
      id: "starts_at",
      header: "Starts",
      priority: 2,
      cell: (row) => orDash(row.starts_at)
    },
    {
      id: "invite_status",
      header: "Invite",
      priority: 3,
      cell: (row) => <Badge variant={inviteStatusTone(row.invite_status)}>{inviteStatusLabel(row.invite_status)}</Badge>
    },
    {
      id: "status",
      header: "Status",
      priority: 2,
      cell: (row) => (
        <StatusDot tone={sessionStatusTone(row.status) === "danger" ? "err" : "info"} label={sessionStatusLabel(row.status)}  />
      )
    }
  ];
}

export type SessionsCollectionFetcher = (
  input: CollectionFetcherInput
) => Promise<CollectionFetcherResult<SessionRow>>;

export function useSessionsCollection(
  fetcher: SessionsCollectionFetcher,
  cohortOptions: CohortFilterOption[] = [],
  routePath?: string
): UseCollectionResult<SessionRow> {
  const filters = useMemo(() => buildSessionsFilterDefs([], cohortOptions, DEFAULT_SESSION_STATUSES), [cohortOptions]);
  const columns = useMemo(() => buildSessionsCollectionColumns(), []);
  const initialState = useMemo(
    () => readDirectoryInitialState(routePath, SESSIONS_PAGE_SIZE),
    [routePath]
  );

  return useCollection<SessionRow>({
    mode: "server",
    fetcher,
    columns,
    filters,
    pageSize: SESSIONS_PAGE_SIZE,
    keepRowsWhileRefreshing: true,
    urlSync: "query",
    views: "coaching-sessions",
    initialState,
    getRowKey: (row) => row.id
  });
}
